import React, { useState, useEffect, useMemo } from 'react';
import CustomContextMenu from '../components/shared/CustomContextMenu';
import { useNavigate } from 'react-router-dom';
import { Ic, ICONS } from '../data/icons';
import { COMPLIANCE_MAP, DEVICE_ICON, DEVICE_COLOR } from '../data/mockData';
import { Badge, StatusDot, MiniBar, Avatar, Card, CardHeader, Modal, toast, SearchInput, TableSkeleton, Pagination, EmptyState, FormField, Input, Select } from '../components/shared';

const agentIcons = [
  { value: 'default', label: 'Varsayılan', emoji: '🟦' },
  { value: 'shield', label: 'Kalkan', emoji: '🛡️' },
  { value: 'gear', label: 'Dişli', emoji: '⚙️' },
];
const osOptions = [
  { value: 'windows', label: 'Windows' },
  { value: 'macos', label: 'macOS' },
  { value: 'linux', label: 'Linux' },
];

import { fetchDevices, createDevice } from '../api/client';

const PER_PAGE = 8;
const DEVICE_TYPES = ['mac','iphone','ipad','windows'];
const TYPE_LABELS  = { mac:'macOS', iphone:'iPhone', ipad:'iPad', windows:'Windows' };
const emptyForm = { name:'', type:'mac', os:'', serial:'', user:'', dept:'Genel', storage:0, battery:0, encrypted:true };

export default function Devices({ C, theme, isMobile }) {
  const { buildAgent } = require('../api/client');
  const [agentBuildLoading, setAgentBuildLoading] = useState(false);
  const [agentDownloadUrl, setAgentDownloadUrl] = useState('');
  const [agentBuildLog, setAgentBuildLog] = useState('');
  const [agentFormErrors, setAgentFormErrors] = useState({});
  const [agentTab, setAgentTab] = useState(0);

  // Agent Builder form state — SADECE şeffaf agent özellikleri
  // Kaldırılan alanlar: invisible, addToHiddenStartup, hideFile, startupWatcher,
  // disableTaskmanager, processSecurity, criticalProcess, processWatcher,
  // fileIntegration, watchdog (bunlar bilinçli olarak desteklenmiyor)
  const [agentForm, setAgentForm] = useState({
    name: '',
    serverUrl: '',
    addToStartup: true,
    icon: 'default',
    os: 'windows',
    ip: '',
    port: '4000',
    group: 'Default',
    heartbeatInterval: '30',
    // Startup (görünür)
    startupName: '',
    // Assembly
    assemblyTitle: '',
    assemblyDescription: '',
    assemblyCompany: '',
    assemblyProduct: '',
    assemblyCopyright: '',
    assemblyVersion: '1.0.0.0',
    assemblyIcon: '',
  });

  const agentIconObj = agentIcons.find(i => i.value === agentForm.icon) || agentIcons[0];

  const validateAgentForm = () => {
    const e = {};
    if (!agentForm.name.trim()) e.name = 'Agent adı zorunlu';
    if (!agentForm.serverUrl.trim()) e.serverUrl = 'Sunucu URL zorunlu';
    return e;
  };

  const handleAgentBuild = async () => {
    const errors = validateAgentForm();
    setAgentFormErrors(errors);
    if (Object.keys(errors).length) return;
    setAgentBuildLoading(true);
    setAgentBuildLog('Agent oluşturuluyor...');
    try {
      const res = await buildAgent(agentForm);
      if (res?.downloadUrl) {
        setAgentDownloadUrl(res.downloadUrl);
        const sizeInfo = res.fileSize ? ` (${res.fileSize})` : '';
        setAgentBuildLog(`✅ ${res.osLabel || agentForm.os} agent hazır${sizeInfo}`);
      } else {
        setAgentBuildLog('❌ Build başarısız: indirme linki dönmedi');
      }
    } catch (err) {
      setAgentBuildLog(`❌ Hata: ${err.message}`);
    } finally {
      setAgentBuildLoading(false);
    }
  };

  // Dosya yükleme modali için
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const handleUpload = async () => {
    if (!uploadFile || !contextMenu.device) return;
    setUploading(true);
    try {
      await new Promise(r => setTimeout(r, 1200));
      toast.success('Dosya başarıyla yüklendi');
      setShowUploadModal(false);
      setUploadFile(null);
    } catch (err) {
      toast.error('Dosya yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anim, setAnim]       = useState(false);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('all');
  const [page, setPage]       = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [agentBuilderOpen, setAgentBuilderOpen] = useState(false);
  const [form, setForm]       = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]   = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, device: null });
  const handleContextMenu = (e, device) => {
    e.preventDefault();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, device });
  };
  const closeContextMenu = () => setContextMenu({ ...contextMenu, visible: false });

  // Modal states — SADECE meşru MDM/destek aksiyonları
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Helper: navigate to device detail
  const goToDeviceDetail = () => {
    if (contextMenu.device) navigate(`/devices/${contextMenu.device.id}`);
  };

  // Helper: send command to device
  const sendDeviceCommand = async (type, payload = {}) => {
    if (!contextMenu.device) return;
    try {
      await import('../api/client').then(api => api.sendCommand(contextMenu.device.id, type, payload));
      toast.success(`${type} komutu gönderildi`);
    } catch (err) {
      toast.error('Komut gönderilemedi');
    }
  };

  // Sağ-tık menüsü — meşru MDM komutları
  // KALDIRILAN seçenekler (RAT pattern): File manager, Process manager, Window manager,
  // Clipboard manager, Registry manager, Startup manager, Command prompt, TCP connection,
  // Remote webcam monitoring, Remote microphone monitoring, Password recovery, Uzak Masaüstü
  // (uzak masaüstü meşru bir destek aracıdır ama MDM kapsamında değildir;
  //  ileride ayrı bir "şeffaf destek bağlantısı" özelliği olarak eklenebilir)
  const contextMenuOptions = contextMenu.device ? [
    { label: 'Cihaz Detayı', onClick: goToDeviceDetail },
    { label: 'Cihazı Kilitle', onClick: () => sendDeviceCommand('lock') },
    { label: 'Yeniden Başlat', onClick: () => sendDeviceCommand('restart') },
    { label: 'Mesaj Gönder', onClick: () => {
        const msg = window.prompt('Cihaza gönderilecek mesaj:');
        if (msg) sendDeviceCommand('message', { message: msg });
      }
    },
    { label: 'Dosya Gönder', onClick: () => setShowUploadModal(true) },
  ] : [];

  useEffect(() => {
    setLoading(true);
    fetchDevices()
      .then(data => { setDevices(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []); })
      .catch(() => toast.error('Cihazlar yüklenemedi'))
      .finally(() => { setLoading(false); setTimeout(() => setAnim(true), 200); });
  }, []);

  const FILTERS = [
    { k:'all',       l:'Tümü' },
    { k:'online',    l:'Çevrimiçi' },
    { k:'compliant', l:'Uyumlu' },
    { k:'breach',    l:'İhlal' },
    { k:'mac',       l:'macOS' },
    { k:'windows',   l:'Windows' },
  ];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return devices.filter(d => {
      const ms = !q || d.name.toLowerCase().includes(q) || (d.user || '').toLowerCase().includes(q) || (d.serial || '').toLowerCase().includes(q);
      const mf = filter==='all' || d.compliance===filter || d.status===filter || d.type===filter;
      return ms && mf;
    });
  }, [devices, search, filter]);

  const paged = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);

  const validate = () => {
    const e = {};
    if (!form.name.trim())   e.name   = 'Cihaz adı zorunlu';
    if (!form.os.trim())     e.os     = 'İşletim sistemi zorunlu';
    if (!form.serial.trim()) e.serial = 'Seri numarası zorunlu';
    else if (devices.find(d => d.serial === form.serial)) e.serial = 'Bu seri numarası zaten kayıtlı';
    setFormErrors(e);
    return !Object.keys(e).length;
  };

  const handleAdd = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const created = await createDevice({ ...form, storage:Number(form.storage), battery:Number(form.battery) });
      setDevices(ds => [...ds, created]);
      toast.success('Cihaz başarıyla eklendi');
      setAddOpen(false);
      setForm(emptyForm);
    } catch (err) { toast.error(err.message || 'Cihaz eklenemedi'); }
    finally { setSaving(false); }
  };

  const osSuggestions = { mac:['macOS 14.3','macOS 14.2','macOS 13.6'], iphone:['iOS 17.3','iOS 17.2','iOS 16.7'], ipad:['iPadOS 17.3','iPadOS 17.2'], windows:['Windows 11 23H2','Windows 11 22H2','Windows 10 22H2'] };

  return (
    <>
      {/* Dosya Gönder Modal */}
      <Modal isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); setUploadFile(null); }} onConfirm={handleUpload} title='Dosya Gönder' type='info' C={C} confirmLabel={uploading ? 'Gönderiliyor...' : 'Gönder'} wide>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <input type='file' onChange={e => setUploadFile(e.target.files[0] || null)} disabled={uploading} style={{ fontSize:14 }} />
          {uploadFile && <div style={{ fontSize:13, color:C.textSub }}>Seçilen dosya: <b>{uploadFile.name}</b></div>}
          <div style={{ fontSize:11, color:C.textFaint, padding:'8px 12px', background:C.surface2, borderRadius:8 }}>
            ℹ️ Dosya cihazın yüklemeler klasörüne gönderilir. Kullanıcı bilgilendirme alır.
          </div>
        </div>
      </Modal>

      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        {/* Toolbar */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
          <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder='Cihaz, kullanıcı veya seri no...' C={C} style={{ flex:'1 1 200px', maxWidth:320 }} />
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {FILTERS.map(f => (
              <button key={f.k} onClick={() => { setFilter(f.k); setPage(1); }} style={{ padding:'7px 12px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:filter===f.k?700:400, background:filter===f.k?C.accentBg:'transparent', color:filter===f.k?C.accent:C.textSub, border:`1px solid ${filter===f.k?C.accentBorder:C.border}`, transition:'all 0.15s' }}>{f.l}</button>
            ))}
          </div>
          <button onClick={() => { setForm(emptyForm); setFormErrors({}); setAddOpen(true); }} style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:7, padding:'8px 18px', borderRadius:9, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
            <Ic d={ICONS.plus} size={15} s='#fff' /> Cihaz Ekle
          </button>
          <button onClick={() => setAgentBuilderOpen(true)} style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 18px', borderRadius:9, border:'none', background:'#222', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
            <Ic d={ICONS.settings || ICONS.gear || ICONS.plus} size={15} s='#fff' /> Agent Builder
          </button>
        </div>

        {/* Agent Builder Modal */}
        <Modal isOpen={agentBuilderOpen} onClose={() => setAgentBuilderOpen(false)} onConfirm={handleAgentBuild} title="Agent Builder (Şeffaf Mod)" type="info" C={C} confirmLabel={agentBuildLoading ? "Oluşturuluyor..." : agentDownloadUrl ? "Tekrar Oluştur" : "Agent Oluştur"} extraWide>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 14,
            alignItems: 'stretch',
            boxSizing: 'border-box',
            minHeight: 380,
            maxHeight: 'calc(80vh - 120px)',
            overflow: 'hidden',
          }}>
            {/* Sol Panel: Özet ve ikon */}
            <div style={{ background:C.surface2, borderRadius:12, padding:'14px 8px', display:'flex', flexDirection:'column', alignItems:'center', gap:10, minWidth:0, width:150, flex:'0 0 150px', overflowY:'auto' }}>
              <div style={{ fontSize:36, marginBottom:4 }}>{agentIconObj.emoji}</div>
              <div style={{ fontWeight:700, fontSize:13, color:C.text, textAlign:'center', wordBreak:'break-word' }}>{agentForm.name || 'Agent Adı'}</div>
              <div style={{ fontSize:11, color:C.textSub, marginBottom:6 }}>
                {({'windows':'🪟 Windows','macos':'🍎 macOS','linux':'🐧 Linux'})[agentForm.os] || 'İşletim Sistemi'}
              </div>
              <div style={{ fontSize:10, color:C.textFaint, textAlign:'center', lineHeight:1.7 }}>
                {agentForm.serverUrl && <div>🔗 {agentForm.serverUrl}</div>}
                {agentForm.addToStartup && <div>🚀 Başlangıçta çalıştır</div>}
                <div>👁️ Şeffaf mod (kullanıcı görür)</div>
              </div>
              {agentBuildLoading && <div style={{ marginTop:18 }}><span className="spinner" style={{ border:'3px solid #eee', borderTop:`3px solid ${C.accent}`, borderRadius:'50%', width:32, height:32, display:'inline-block', animation:'spin 1s linear infinite' }} /></div>}
              {agentDownloadUrl && (
                <div style={{ textAlign:'center', marginTop:8 }}>
                  <a href={agentDownloadUrl} download style={{ fontWeight:700, color:'#22c55e', fontSize:12, display:'block', textDecoration:'none', padding:'6px 10px', borderRadius:8, background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.3)' }}>
                    ⬇ İndir
                  </a>
                </div>
              )}
              {agentBuildLog && (
                <div style={{ fontSize:10.5, color: agentBuildLog.includes('Hata') || agentBuildLog.includes('❌') ? C.red : agentBuildLog.includes('✅') ? C.green : C.textSub, marginTop:6, textAlign:'center', lineHeight:1.5, padding:'4px 6px', background:C.surface, borderRadius:6 }}>
                  {agentBuildLog}
                </div>
              )}
            </div>

            {/* Sağ Panel: Sekmeli Ayarlar */}
            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:0, minWidth:0, overflow:'hidden' }}>
              {/* Sekmeler — "Modül Koruma" sekmesi kaldırıldı */}
              <div style={{ display:'flex', gap:0, borderBottom:`1px solid ${C.border}`, marginBottom:8, flexWrap:'nowrap', overflowX:'auto', flexShrink:0 }}>
                {['Kimlik', 'Ağ', 'Başlangıç', 'Assembly'].map((tab, i) => (
                  <button key={tab} onClick={()=>setAgentTab(i)} style={{
                    padding:'8px 14px',
                    fontWeight: agentTab===i ? 700 : 500,
                    fontSize:12.5,
                    color: agentTab===i ? C.accent : C.textSub,
                    background:'none',
                    border:'none',
                    borderBottom: agentTab===i ? `2px solid ${C.accent}` : '2px solid transparent',
                    cursor:'pointer',
                    transition:'all 0.15s',
                    outline:'none',
                    whiteSpace:'nowrap',
                    flexShrink:0,
                  }}>{tab}</button>
                ))}
              </div>
              <div style={{ padding:'10px 8px 0 8px', flex:1, minHeight:0, overflowY:'auto', overflowX:'hidden' }}>
                {/* Kimlik */}
                {agentTab===0 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                    <div>
                      <div style={{ fontSize:11.5, fontWeight:700, color:C.textFaint, marginBottom:8, letterSpacing:'0.5px' }}>HEDEF İŞLETİM SİSTEMİ</div>
                      <div style={{ display:'flex', gap:8 }}>
                        {[
                          { v:'windows', l:'Windows', icon:'🪟', desc:'x64 .exe' },
                          { v:'macos',   l:'macOS',   icon:'🍎', desc:'Universal binary' },
                          { v:'linux',   l:'Linux',   icon:'🐧', desc:'x64 binary' },
                        ].map(o => (
                          <button key={o.v} onClick={()=>setAgentForm(f=>({...f,os:o.v}))} style={{
                            flex:1, padding:'10px 8px', borderRadius:10, cursor:'pointer', textAlign:'center',
                            border:`2px solid ${agentForm.os===o.v ? C.accent : C.border}`,
                            background: agentForm.os===o.v ? C.accentBg : 'transparent',
                            transition:'all 0.15s',
                          }}>
                            <div style={{ fontSize:22 }}>{o.icon}</div>
                            <div style={{ fontSize:12.5, fontWeight:700, color:agentForm.os===o.v?C.accent:C.text, marginTop:4 }}>{o.l}</div>
                            <div style={{ fontSize:10, color:C.textFaint, marginTop:2 }}>{o.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                      <FormField label="Agent Adı" C={C} error={agentFormErrors.name}>
                        <Input C={C} value={agentForm.name} onChange={e=>setAgentForm(f=>({...f, name: e.target.value}))} placeholder="Şirket veya servis adı..." />
                      </FormField>
                      <FormField label="Heartbeat Süresi (sn)" C={C}>
                        <Input C={C} type="number" value={agentForm.heartbeatInterval} onChange={e=>setAgentForm(f=>({...f, heartbeatInterval: e.target.value}))} placeholder="30" />
                      </FormField>
                    </div>
                    <FormField label="Sunucu URL" C={C} error={agentFormErrors.serverUrl}>
                      <Input C={C} value={agentForm.serverUrl} onChange={e=>setAgentForm(f=>({...f, serverUrl: e.target.value}))} placeholder="https://mdm.sirket.com" />
                    </FormField>
                    <div style={{ fontSize:12, color:C.textSub, padding:'10px 14px', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:8, lineHeight:1.55 }}>
                      ✅ <b>Şeffaf mod:</b> Bu agent kullanıcının görebileceği şekilde çalışır.
                      Görev Yöneticisi'nde, Aktivite İzleyici'de ve başlangıç programları listesinde görünür.
                      Gizli mod, dosya gizleme, görev yöneticisi devre dışı bırakma gibi özellikler
                      kurumsal güvenlik politikamız ve KVKK gereği bilinçli olarak desteklenmemektedir.
                    </div>
                  </div>
                )}

                {/* Ağ */}
                {agentTab===1 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                    <div style={{ fontSize:11.5, color:C.textFaint, background:C.surface2, borderRadius:8, padding:'8px 12px' }}>
                      💡 IP ve Port'u girin — Sunucu URL otomatik oluşturulur ve Kimlik sekmesindeki URL'yi günceller.
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14 }}>
                      <FormField label="Sunucu IP / Domain" C={C}>
                        <Input C={C} value={agentForm.ip||''} onChange={e=>{
                          const ip = e.target.value;
                          const port = agentForm.port || '4000';
                          setAgentForm(f=>({...f, ip, serverUrl: ip ? `https://${ip}:${port}` : f.serverUrl}));
                        }} placeholder="mdm.sirket.com veya 10.20.30.40" />
                      </FormField>
                      <FormField label="Port" C={C}>
                        <Input C={C} type="number" value={agentForm.port||''} onChange={e=>{
                          const port = e.target.value;
                          const ip = agentForm.ip;
                          setAgentForm(f=>({...f, port, serverUrl: ip ? `https://${ip}:${port}` : f.serverUrl}));
                        }} placeholder="4000" />
                      </FormField>
                    </div>
                    <FormField label="Client Group" C={C}>
                      <Input C={C} value={agentForm.group||''} onChange={e=>setAgentForm(f=>({...f, group: e.target.value}))} placeholder="Default" />
                    </FormField>
                    {agentForm.serverUrl && (
                      <div style={{ fontSize:12, color:C.accent, background:C.accentBg, borderRadius:8, padding:'8px 12px', border:`1px solid ${C.accentBorder}` }}>
                        🔗 Sunucu URL: <b>{agentForm.serverUrl}</b>
                      </div>
                    )}
                  </div>
                )}

                {/* Başlangıç — sadece görünür kayıt */}
                {agentTab===2 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                    <FormField label="Başlangıçta Çalıştır" C={C}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <input type="checkbox" checked={agentForm.addToStartup||false} onChange={e=>setAgentForm(f=>({...f, addToStartup: e.target.checked}))} />
                        <span style={{ fontSize:12.5, color:C.textSub }}>
                          Görünür şekilde Registry Run / LaunchAgent / systemd kaydı oluştur
                        </span>
                      </div>
                    </FormField>
                    <FormField label="Startup Kayıt Adı" C={C}>
                      <Input C={C} value={agentForm.startupName||''} onChange={e=>setAgentForm(f=>({...f, startupName: e.target.value}))} placeholder="ODOMDMAgent" />
                    </FormField>
                    <div style={{ fontSize:11.5, color:C.textFaint, padding:'10px 12px', background:C.surface2, borderRadius:8, lineHeight:1.6 }}>
                      ℹ️ Bu kayıt Windows Görev Yöneticisi → Başlangıç sekmesinde, macOS'ta
                      <code style={{ background:C.bg, padding:'1px 5px', borderRadius:4, margin:'0 4px' }}>launchctl list</code>
                      komutuyla, Linux'ta
                      <code style={{ background:C.bg, padding:'1px 5px', borderRadius:4, margin:'0 4px' }}>systemctl --user list-units</code>
                      ile görünür. Kullanıcı kaldırma yetkisine sahiptir.
                    </div>
                  </div>
                )}

                {/* Assembly */}
                {agentTab===3 && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18 }}>
                    <FormField label="Title" C={C}>
                      <Input C={C} value={agentForm.assemblyTitle||''} onChange={e=>setAgentForm(f=>({...f, assemblyTitle: e.target.value}))} placeholder="ODO MDM Agent" />
                    </FormField>
                    <FormField label="Description" C={C}>
                      <Input C={C} value={agentForm.assemblyDescription||''} onChange={e=>setAgentForm(f=>({...f, assemblyDescription: e.target.value}))} placeholder="Acıbadem MDM endpoint agent" />
                    </FormField>
                    <FormField label="Company" C={C}>
                      <Input C={C} value={agentForm.assemblyCompany||''} onChange={e=>setAgentForm(f=>({...f, assemblyCompany: e.target.value}))} placeholder="Acıbadem" />
                    </FormField>
                    <FormField label="Product" C={C}>
                      <Input C={C} value={agentForm.assemblyProduct||''} onChange={e=>setAgentForm(f=>({...f, assemblyProduct: e.target.value}))} placeholder="ODO MDM" />
                    </FormField>
                    <FormField label="Copyright" C={C}>
                      <Input C={C} value={agentForm.assemblyCopyright||''} onChange={e=>setAgentForm(f=>({...f, assemblyCopyright: e.target.value}))} placeholder="© Acıbadem 2026" />
                    </FormField>
                    <FormField label="Version" C={C}>
                      <Input C={C} value={agentForm.assemblyVersion||''} onChange={e=>setAgentForm(f=>({...f, assemblyVersion: e.target.value}))} placeholder="1.0.0.0" />
                    </FormField>
                    <FormField label="Icon" C={C}>
                      <Input C={C} value={agentForm.assemblyIcon||''} onChange={e=>setAgentForm(f=>({...f, assemblyIcon: e.target.value}))} placeholder="" />
                    </FormField>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>

        {/* Table */}
        <Card C={C}>
          <CardHeader C={C} title='Cihaz Listesi' action={<span style={{ fontSize:11, color:C.textFaint }}>{filtered.length} cihaz</span>} />
          {loading ? <TableSkeleton rows={7} cols={7} C={C} /> : (
            <>
              {contextMenu.visible && contextMenu.device && (
                <CustomContextMenu
                  x={contextMenu.x}
                  y={contextMenu.y}
                  options={contextMenuOptions}
                  onClose={closeContextMenu}
                  C={C}
                />
              )}
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:720 }}>
                  <thead>
                    <tr style={{ background:C.surface2 }}>
                      {['Cihaz','Kullanıcı','Şifreleme','Depolama','Durum','Uyumluluk','İşlemler'].map(h => (
                        <th key={h} style={{ textAlign:'left', padding:'10px 16px', fontSize:10.5, fontWeight:700, color:C.textFaint, letterSpacing:'0.7px', textTransform:'uppercase', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map(d => {
                      const [cc,cl] = COMPLIANCE_MAP[d.compliance] || ['#94a3b8', d.compliance];
                      const sc = d.storage>80 ? C.red : d.storage>60 ? C.amber : C.green;
                      const goDetail = () => navigate(`/devices/${d.id}`);
                      return (
                        <tr key={d.id}
                          style={{ cursor:'pointer', transition:'background 0.1s' }}
                          onClick={goDetail}
                          onContextMenu={e => handleContextMenu(e, d)}
                          onMouseEnter={e=>e.currentTarget.style.background=C.surface2}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                              <div style={{ width:32, height:32, borderRadius:8, flexShrink:0, background:`${DEVICE_COLOR[d.type]}18`, display:'flex', alignItems:'center', justifyContent:'center', color:DEVICE_COLOR[d.type] }}>
                                <Ic d={ICONS[DEVICE_ICON[d.type]]} size={15} />
                              </div>
                              <div>
                                <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{d.name}</div>
                                <div style={{ fontSize:10, color:C.textFaint }}>{d.os} · {d.serial}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                              <Avatar initials={d.user ? d.user.split('.').map(s=>s[0].toUpperCase()).join('') : '??'} size={24} />
                              <div>
                                <div style={{ fontSize:12.5, color:C.text }}>{d.user || 'Bilinmeyen'}</div>
                                <div style={{ fontSize:10, color:C.textFaint }}>{d.dept || '-'}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}>
                            <Badge color={d.encrypted?C.green:C.red}>{d.encrypted?'🔒 Şifreli':'⚠️ Açık'}</Badge>
                          </td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}`, minWidth:110 }}>
                            <div style={{ fontSize:11, color:C.textSub, marginBottom:5, fontWeight:600 }}>{d.storage}%</div>
                            <MiniBar pct={anim?d.storage:0} color={sc} C={C} />
                          </td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:C.textSub }}>
                              <StatusDot online={d.status==='online'} />
                              {d.status==='online'?'Çevrimiçi':'Çevrimdışı'}
                            </div>
                            <div style={{ fontSize:10, color:C.textFaint, marginTop:2 }}>{d.lastSeen} önce</div>
                          </td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}>
                            <Badge color={cc}>{cl}</Badge>
                          </td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }} onClick={e=>e.stopPropagation()}>
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={goDetail} style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${C.border}`, cursor:'pointer', fontSize:11, background:'transparent', color:C.textSub, fontWeight:600 }}>Detay</button>
                              <button onClick={() => { setContextMenu({ visible:false, device:d }); sendDeviceCommand('lock'); }} style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${C.red}44`, cursor:'pointer', fontSize:11, background:C.redBg, color:C.red, fontWeight:600 }}>Kilitle</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filtered.length===0 && <EmptyState icon='devices' title='Cihaz bulunamadı' desc='Arama veya filtre kriterlerini değiştirin' C={C} />}
              <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onChange={setPage} C={C} />
            </>
          )}
        </Card>

        {/* Add Device Modal */}
        <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} onConfirm={handleAdd} title='Yeni Cihaz Ekle' type='info' C={C} confirmLabel={saving?'Ekleniyor...':'Cihaz Ekle'} wide>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <FormField label='Cihaz Adı' required C={C} error={formErrors.name}>
                <Input C={C} error={formErrors.name} value={form.name} onChange={e=>{setForm(f=>({...f,name:e.target.value}));setFormErrors(fe=>({...fe,name:''}));}} placeholder='MacBook Pro 16"' />
              </FormField>
              <FormField label='Platform / Tür' C={C}>
                <Select C={C} value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value,os:''}))}>
                  {DEVICE_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </Select>
              </FormField>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <FormField label='İşletim Sistemi' required C={C} error={formErrors.os}>
                <Select C={C} value={form.os} onChange={e=>{setForm(f=>({...f,os:e.target.value}));setFormErrors(fe=>({...fe,os:''}));}} style={{ borderColor:formErrors.os?C.red:undefined }}>
                  <option value=''>Seçin...</option>
                  {(osSuggestions[form.type]||[]).map(o => <option key={o} value={o}>{o}</option>)}
                </Select>
              </FormField>
              <FormField label='Seri Numarası' required C={C} error={formErrors.serial}>
                <Input C={C} error={formErrors.serial} value={form.serial} onChange={e=>{setForm(f=>({...f,serial:e.target.value.toUpperCase()}));setFormErrors(fe=>({...fe,serial:''}));}} placeholder='C02XL2LMJG5H' />
              </FormField>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <FormField label='Kullanıcı (kullanici.adi)' C={C}>
                <Input C={C} value={form.user} onChange={e=>setForm(f=>({...f,user:e.target.value}))} placeholder='ahmet.kaya' />
              </FormField>
              <FormField label='Departman' C={C}>
                <Select C={C} value={form.dept} onChange={e=>setForm(f=>({...f,dept:e.target.value}))}>
                  {['Mühendislik','Pazarlama','Satış','Finans','İK','Tasarım','Hukuk','Operasyon','Genel'].map(d => <option key={d} value={d}>{d}</option>)}
                </Select>
              </FormField>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
              <FormField label='Depolama %' C={C}>
                <Input C={C} type='number' min='0' max='100' value={form.storage} onChange={e=>setForm(f=>({...f,storage:e.target.value}))} />
              </FormField>
              <FormField label='Pil %' C={C}>
                <Input C={C} type='number' min='0' max='100' value={form.battery} onChange={e=>setForm(f=>({...f,battery:e.target.value}))} />
              </FormField>
              <FormField label='Disk Şifreleme' C={C}>
                <Select C={C} value={form.encrypted?'1':'0'} onChange={e=>setForm(f=>({...f,encrypted:e.target.value==='1'}))}>
                  <option value='1'>✅ Şifreli</option>
                  <option value='0'>❌ Açık</option>
                </Select>
              </FormField>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
}
